/**
 * The WMS permission catalog and the EMS-role → permission map.
 *
 * Curated from the old WMS `seed-data/auth/{permissions,ems_role_permissions}.json`
 * (186 keys / 410 grants). Kept: the dot-namespace style and every key that maps
 * onto a module we actually build. Dropped: the ~110 never-enforced keys copied
 * from the previous vendor product — all of `economy.*`, `admin.goodsOwner*`,
 * `admin.dangerousGoods.*`, `admin.shipment.*` catalogs, `admin.materials.*`,
 * `registers.types.*`, `registers.automation.*`, `statistics.*` lobby/period
 * splits, `admin.units.*`, `system.changePassword` (EMS owns credentials).
 * Added per the build brief: `warehouse.print`, `warehouse.barcodes.teach`,
 * `orders.staff.create`, `orders.outbound.deliverySchedule`.
 * Added at Wave-1 reconciliation: `notifications.read` (personal data, granted to
 * EVERY role — a notification is not a privilege) and `devices.heartbeat` (the
 * scanner telemetry beat, previously ORed against five unrelated floor keys).
 *
 * Enforcement is SERVER-SIDE on every endpoint (`@Permissions(...)`, OR
 * semantics). The frontend uses these keys only to hide chrome — the old WMS
 * enforced them nowhere, which is the bug this replaces.
 *
 * `super_admin` is NOT in `ROLE_PERMISSIONS`: it bypasses in code
 * (PermissionsGuard short-circuit), so the seed never has to be re-run when a
 * key is added.
 */

export interface PermissionDefinition {
  key: string;
  description: string;
}

export const PERMISSIONS = [
  // --- administration -----------------------------------------------------
  { key: 'admin.users', description: 'View users provisioned from EMS' },
  { key: 'admin.permissions', description: 'Edit the role → permission matrix' },
  { key: 'admin.settings.system', description: 'Edit system settings (app_settings)' },
  { key: 'admin.settings.printers', description: 'Manage the printer registry' },
  { key: 'admin.logs.audit', description: 'Read the audit log' },
  { key: 'admin.logs.events', description: 'Read the event log' },
  { key: 'admin.logs.mail', description: 'Read the outgoing mail log' },
  { key: 'admin.logs.print', description: 'Read print job history' },
  { key: 'admin.integration.integrations', description: 'View integration configuration' },
  { key: 'admin.integration.webhooks', description: 'Manage inbound/outbound webhooks' },

  // --- master data (registers) -------------------------------------------
  { key: 'registers.articles.read', description: 'View the article register' },
  { key: 'registers.articles.manage', description: 'Create and edit articles' },
  { key: 'registers.articles.categories', description: 'Manage article categories' },
  { key: 'registers.articles.units', description: 'Manage units and per-article conversions' },
  { key: 'registers.barcodes.manage', description: 'Manage article barcodes (GTIN and supplier-scoped)' },
  { key: 'registers.suppliers.read', description: 'View suppliers' },
  { key: 'registers.suppliers.manage', description: 'Create and edit suppliers' },
  { key: 'registers.restaurants.read', description: 'View restaurants' },
  { key: 'registers.restaurants.manage', description: 'Create and edit restaurants' },
  { key: 'registers.restaurantUsers.manage', description: 'Map users to restaurants' },

  // --- warehouse ----------------------------------------------------------
  { key: 'warehouse.map', description: 'View the warehouse map' },
  { key: 'warehouse.map.manage', description: 'Build and edit the warehouse map (areas, shelves, bins)' },
  { key: 'warehouse.locations', description: 'View bins and their contents' },
  { key: 'warehouse.locations.manage', description: 'Edit bin properties (pick location, blocked)' },
  { key: 'warehouse.stock.read', description: 'View stock balances' },
  { key: 'warehouse.stock.movements', description: 'View the stock movement ledger' },
  { key: 'warehouse.goods.receive', description: 'Receive goods against a PO or blind' },
  { key: 'warehouse.goods.putaway', description: 'Put away received goods' },
  { key: 'warehouse.goods.pick', description: 'Pick goods' },
  { key: 'warehouse.goods.move', description: 'Move pallets and goods internally' },
  { key: 'warehouse.goods.deliver', description: 'Hand goods over for dispatch' },
  { key: 'warehouse.pallets.manage', description: 'Create, merge and close pallets (LPN)' },
  { key: 'warehouse.lots.manage', description: 'Create and edit lots / best-before dates' },
  { key: 'warehouse.assets.manage', description: 'Manage serialized assets (CO2 tubes, cages)' },
  { key: 'warehouse.inventory.task', description: 'Create and manage stocktakes' },
  { key: 'warehouse.inventory.count', description: 'Count during a stocktake' },
  { key: 'warehouse.inventory.adjust', description: 'Approve and post inventory adjustments' },
  { key: 'warehouse.barcodes.teach', description: 'Teach an unknown barcode on first scan' },
  { key: 'warehouse.print', description: 'Print labels and warehouse documents' },
  { key: 'warehouse.replenishment.view', description: 'View replenishment suggestions' },
  { key: 'warehouse.replenishment.createPo', description: 'Create draft purchase orders from suggestions' },

  // --- orders -------------------------------------------------------------
  { key: 'orders.outbound.list', description: 'View sales orders' },
  { key: 'orders.outbound.create', description: 'Create sales orders' },
  { key: 'orders.outbound.manage', description: 'Edit sales orders and change status' },
  { key: 'orders.outbound.cancel', description: 'Cancel sales orders' },
  { key: 'orders.outbound.deliverySchedule', description: 'Manage delivery schedules and custom delivery dates' },
  { key: 'orders.outbound.bypassCutoff', description: 'Update an order after the order-by cutoff' },
  { key: 'orders.outbound.viewAllLocations', description: 'See orders for every restaurant, not just your own' },
  { key: 'orders.staff.create', description: 'Create staff orders (on-site collection)' },
  { key: 'orders.staff.manage', description: 'Manage staff orders and mark them collected' },
  { key: 'orders.inbound.list', description: 'View purchase orders' },
  { key: 'orders.inbound.create', description: 'Create purchase orders' },
  { key: 'orders.inbound.manage', description: 'Edit purchase orders and close receipts' },
  { key: 'orders.portal.order', description: 'Place orders in the restaurant portal' },

  // --- picking, packing, shipping ----------------------------------------
  { key: 'picking.flows.manage', description: 'Configure picking flows (location sequence)' },
  { key: 'picking.rounds.read', description: 'View pick rounds and tasks' },
  { key: 'picking.rounds.manage', description: 'Build, stage and close pick rounds' },
  { key: 'picking.rounds.pick', description: 'Claim and confirm pick tasks' },
  { key: 'picking.packing', description: 'Pack orders and assign pallets to shipments' },
  { key: 'shipping.shipments.read', description: 'View shipments' },
  { key: 'shipping.shipments.book', description: 'Book transport (carrier or own truck)' },
  { key: 'shipping.shipments.cancel', description: 'Cancel a booked shipment' },
  { key: 'shipping.reception.confirm', description: 'Confirm goods reception at the restaurant' },

  // --- production ---------------------------------------------------------
  { key: 'production.orders.read', description: 'View production orders' },
  { key: 'production.orders.manage', description: 'Create, plan and release production orders' },
  { key: 'production.orders.report', description: 'Report production output and consumption' },
  { key: 'production.tasks.manage', description: 'Manage the production task board' },
  { key: 'production.requests.create', description: 'Request goods from the warehouse' },
  { key: 'production.requests.manage', description: 'Accept and fulfil production goods requests' },

  // --- claims -------------------------------------------------------------
  { key: 'claims.read', description: 'View claims' },
  { key: 'claims.create', description: 'Register a claim' },
  { key: 'claims.manage', description: 'Change claim status and resolve claims' },
  { key: 'claims.statistics', description: 'View claim statistics' },

  // --- devices ------------------------------------------------------------
  { key: 'devices.read', description: 'View the scanner fleet and its status' },
  { key: 'devices.manage', description: 'Register, rename and deactivate scanners' },
  { key: 'devices.heartbeat', description: 'Report scanner telemetry (battery, wifi, outbox depth)' },
  { key: 'devices.printJobs.read', description: 'View and reprint print jobs' },

  // --- notifications ------------------------------------------------------
  { key: 'notifications.read', description: 'Read and acknowledge your own notifications' },

  // --- ERP integration ----------------------------------------------------
  { key: 'integration.sync.read', description: 'View Business Central sync status, inbox and outbox' },
  { key: 'integration.sync.trigger', description: 'Trigger a manual resync or retry a failed message' },
] as const satisfies readonly PermissionDefinition[];

export type PermissionKey = (typeof PERMISSIONS)[number]['key'];

export const PERMISSION_KEYS: readonly PermissionKey[] = PERMISSIONS.map((p) => p.key);

/** EMS role keys, canonicalized to lowercase by the API on every ingress path. */
export const ROLE_KEYS = [
  'super_admin',
  'admin',
  'lager',
  'produktion',
  'arbetsledare',
  'ekonomi',
  'bestallare',
] as const;
export type RoleKey = (typeof ROLE_KEYS)[number];

/** Bypasses every permission check in `PermissionsGuard` — never seeded. */
export const SUPER_ADMIN_ROLE_KEY: RoleKey = 'super_admin';

const ADMIN_PERMISSIONS: readonly PermissionKey[] = PERMISSION_KEYS;

const LAGER: readonly PermissionKey[] = [
  'admin.logs.print',
  // NOT admin.settings.printers: that key registers/repoints printers to
  // arbitrary host:port, which the print worker then opens a raw TCP socket to
  // (SSRF). Operators get `warehouse.print` (below) to PRINT; only admins
  // manage the registry. (E2E-found: lager could point a printer at
  // 169.254.169.254.)
  'registers.articles.read',
  'registers.suppliers.read',
  'registers.restaurants.read',
  'warehouse.map',
  'warehouse.locations',
  'warehouse.locations.manage',
  'warehouse.stock.read',
  'warehouse.stock.movements',
  'warehouse.goods.receive',
  'warehouse.goods.putaway',
  'warehouse.goods.pick',
  'warehouse.goods.move',
  'warehouse.goods.deliver',
  'warehouse.pallets.manage',
  'warehouse.lots.manage',
  'warehouse.assets.manage',
  'warehouse.inventory.task',
  'warehouse.inventory.count',
  'warehouse.print',
  'warehouse.replenishment.view',
  'orders.outbound.list',
  'orders.outbound.manage',
  'orders.outbound.viewAllLocations',
  'orders.inbound.list',
  'orders.inbound.manage',
  'picking.rounds.read',
  'picking.rounds.manage',
  'picking.rounds.pick',
  'picking.packing',
  'shipping.shipments.read',
  'shipping.shipments.book',
  'production.orders.read',
  'production.requests.manage',
  'claims.read',
  'claims.create',
  'devices.read',
  'devices.heartbeat',
  'devices.printJobs.read',
  'notifications.read',
];

const ARBETSLEDARE: readonly PermissionKey[] = [
  ...LAGER,
  'admin.logs.audit',
  'admin.logs.events',
  'registers.articles.manage',
  'registers.articles.categories',
  'registers.articles.units',
  'registers.barcodes.manage',
  'registers.suppliers.manage',
  'warehouse.map.manage',
  'warehouse.inventory.adjust',
  'warehouse.barcodes.teach',
  'warehouse.replenishment.createPo',
  'orders.outbound.create',
  'orders.outbound.cancel',
  'orders.outbound.deliverySchedule',
  'orders.outbound.bypassCutoff',
  'orders.staff.create',
  'orders.staff.manage',
  'orders.inbound.create',
  'picking.flows.manage',
  'shipping.shipments.cancel',
  'production.orders.manage',
  'production.tasks.manage',
  'claims.manage',
  'claims.statistics',
  'devices.manage',
  'integration.sync.read',
];

const PRODUKTION: readonly PermissionKey[] = [
  'registers.articles.read',
  'warehouse.map',
  'warehouse.locations',
  'warehouse.stock.read',
  'warehouse.stock.movements',
  'warehouse.goods.move',
  'warehouse.goods.pick',
  'warehouse.lots.manage',
  'warehouse.pallets.manage',
  'warehouse.inventory.count',
  'warehouse.print',
  'orders.outbound.list',
  'production.orders.read',
  'production.orders.manage',
  'production.orders.report',
  'production.tasks.manage',
  'production.requests.create',
  'production.requests.manage',
  'claims.read',
  'claims.create',
  'devices.heartbeat',
  'notifications.read',
];

const EKONOMI: readonly PermissionKey[] = [
  'admin.logs.mail',
  'registers.articles.read',
  'registers.suppliers.read',
  'registers.restaurants.read',
  'warehouse.stock.read',
  'warehouse.stock.movements',
  'orders.outbound.list',
  'orders.outbound.manage',
  'orders.outbound.deliverySchedule',
  'orders.outbound.viewAllLocations',
  'orders.inbound.list',
  'shipping.shipments.read',
  'claims.read',
  'claims.statistics',
  'integration.sync.read',
  'notifications.read',
];

/** Restaurant orderers: scoped to their own restaurants by row-level checks. */
const BESTALLARE: readonly PermissionKey[] = [
  'registers.articles.read',
  'orders.portal.order',
  'orders.outbound.list',
  'orders.outbound.create',
  'shipping.shipments.read',
  'shipping.reception.confirm',
  'claims.read',
  'claims.create',
  'notifications.read',
];

/**
 * Seeded into `role_permissions` (idempotent upsert by (role, permission)).
 * `super_admin` is intentionally absent — see SUPER_ADMIN_ROLE_KEY.
 */
export const ROLE_PERMISSIONS: Readonly<Record<Exclude<RoleKey, 'super_admin'>, readonly PermissionKey[]>> = {
  admin: ADMIN_PERMISSIONS,
  lager: dedupe(LAGER),
  arbetsledare: dedupe(ARBETSLEDARE),
  produktion: dedupe(PRODUKTION),
  ekonomi: dedupe(EKONOMI),
  bestallare: dedupe(BESTALLARE),
};

function dedupe(keys: readonly PermissionKey[]): readonly PermissionKey[] {
  return [...new Set(keys)];
}

/** Canonical form used everywhere: EMS emits `LAGER_ADMIN`, we store `lager_admin`. */
export function normalizeRoleKey(raw: string): string {
  return raw.trim().toLowerCase();
}

export function normalizeRoleKeys(raw: readonly unknown[] | null | undefined): string[] {
  if (!Array.isArray(raw)) return [];
  return [
    ...new Set(
      raw
        .filter((r): r is string => typeof r === 'string')
        .map(normalizeRoleKey)
        .filter((r) => r.length > 0),
    ),
  ];
}

export function isSuperAdmin(roleKeys: readonly string[]): boolean {
  return roleKeys.includes(SUPER_ADMIN_ROLE_KEY);
}
